import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { Eye, FileText, Edit, Trash2, FileDown, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';
import { getCloudFrontUrl } from '../../utils/s3Upload';

// invoices_details.status → chip. Anything unknown falls back to unpaid so a
// new backend status never renders blank.
const INVOICE_STATUS_STYLES = {
  paid: {
    label: 'Paid',
    chip: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    dot: 'bg-emerald-500',
  },
  partially_paid: {
    label: 'Partially Paid',
    chip: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    dot: 'bg-amber-500',
  },
  unpaid: {
    label: 'Unpaid',
    chip: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    dot: 'bg-red-500',
  },
};

// One place that decides an invoice's money figures, so the Paid / Balance
// cells and the totals row can never drift apart.
const getInvoiceMoney = (invRecord) => {
  const raw = invRecord && (Array.isArray(invRecord) ? invRecord[0] : invRecord);
  if (!raw) return null;
  const inv = raw.invoice || raw;
  const summary = inv.summary || inv;
  const status = String(raw.status ?? inv.status ?? 'unpaid').toLowerCase();
  const grandTotal = Number(summary.grand_total ?? summary.grandTotal ?? 0) || 0;
  const partial = Number(raw.partialPayment ?? raw.partial_payment ?? inv.partialPayment ?? inv.partial_payment ?? 0) || 0;
  // A fully paid invoice may have partial_payment left at 0 by the backend.
  const paidSoFar = status === 'paid' ? Math.max(partial, grandTotal) : partial;
  const firstEntry = Array.isArray(inv.invoiceData) ? inv.invoiceData[0] : Array.isArray(inv.invoice_data) ? inv.invoice_data[0] : null;
  const invoiceDate = raw.invoiceDate ?? raw.invoice_date ?? inv.invoiceDate ?? inv.invoice_date
    ?? firstEntry?.invoiceDate ?? firstEntry?.invoice_date ?? null;
  return { status, grandTotal, paidSoFar, balance: Math.max(grandTotal - paidSoFar, 0), invoiceDate };
};

// The generated invoice numbers on a record, newest first. Air invoices carry
// invoice_data as its own column; sea invoices only have it nested inside the
// `invoice` JSON, so read both.
const getInvoiceNumbers = (record) => {
  const raw = record && (Array.isArray(record) ? record[0] : record);
  if (!raw) return [];
  const sources = [raw.invoiceData, raw.invoice_data, raw.invoice?.invoice_data, raw.invoice?.invoiceData];
  const entries = sources.flatMap((src) => {
    let parsed = src;
    if (typeof src === 'string') { try { parsed = JSON.parse(src); } catch { parsed = []; } }
    return Array.isArray(parsed) ? parsed : [];
  });
  const seen = new Set();
  return entries
    .map((e) => String(e?.invoiceNumber || e?.invoice_number || '').trim())
    .filter((n) => n && !seen.has(n) && seen.add(n));
};

const formatInvoiceDate = (value) => {
  const d = new Date(String(value).split(/[T ]/)[0]);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('en-GB');
};

// Shipment numeric fields arrive as numbers, "1,234", arrays, or "12 & 8"
// (multi-container) — add every part up.
const toNumericTotal = (value) => {
  if (value === null || value === undefined || value === '' || value === '-') return 0;
  const parts = Array.isArray(value) ? value : String(value).split(/\s*&\s*/);
  return parts.reduce((acc, part) => {
    const n = Number(String(part).replace(/,/g, '').trim());
    return acc + (Number.isFinite(n) ? n : 0);
  }, 0);
};

/* An AWB is written with spaces and hyphens in some places and without them in
   others, so a map keyed by it is read through this rather than directly. */
const bareAwb = (v) => String(v || '').replace(/[^0-9a-z]/gi, '');
const purOf = (map, shipment) => map[bareAwb(shipment.awbNumber)] || map[bareAwb(shipment.awbNo)] || null;

const ShipmentsTable = ({ shipments, userRole, onEdit, onDelete, onGenerateInvoice, onEditInvoice, onUpdatePayment, invoiceMap = {}, purMap = {}, onViewPur, highlightedAwb = null, showTotals = false, flush = false }) => {
  const [sortOrder, setSortOrder] = useState('desc');   // 'none' | 'asc' | 'desc'
  const [sortBy, setSortBy] = useState('sbDate');       // 'sbDate' | 'invoiceDate'
  const [invTooltip, setInvTooltip] = useState(null);
  const topScrollRef = useRef(null);
  const bottomScrollRef = useRef(null);
  const topScrollContentRef = useRef(null);
  const tableRef = useRef(null);
  const isScrollingRef = useRef(false);
  const highlightRowRef = useRef(null);

  // Scroll to highlighted row when highlightedAwb changes
  useEffect(() => {
    if (highlightedAwb && highlightRowRef.current) {
      highlightRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightedAwb]);

  // Sync width of top scrollbar with table
  useEffect(() => {
    const updateScrollWidth = () => {
      if (tableRef.current && topScrollContentRef.current) {
        const tableWidth = tableRef.current.scrollWidth;
        topScrollContentRef.current.style.width = `${tableWidth}px`;
      }
    };
    
    updateScrollWidth();
    window.addEventListener('resize', updateScrollWidth);
    
    return () => {
      window.removeEventListener('resize', updateScrollWidth);
    };
  }, [shipments]);
  
  // Sync scroll between top and bottom scrollbars
  useEffect(() => {
    const topScroll = topScrollRef.current;
    const bottomScroll = bottomScrollRef.current;
    
    if (!topScroll || !bottomScroll) return;
    
    const handleTopScroll = () => {
      if (!isScrollingRef.current && bottomScroll) {
        isScrollingRef.current = true;
        bottomScroll.scrollLeft = topScroll.scrollLeft;
        requestAnimationFrame(() => {
          isScrollingRef.current = false;
        });
      }
    };
    
    const handleBottomScroll = () => {
      if (!isScrollingRef.current && topScroll) {
        isScrollingRef.current = true;
        topScroll.scrollLeft = bottomScroll.scrollLeft;
        requestAnimationFrame(() => {
          isScrollingRef.current = false;
        });
      }
    };
    
    topScroll.addEventListener('scroll', handleTopScroll);
    bottomScroll.addEventListener('scroll', handleBottomScroll);
    
    return () => {
      topScroll.removeEventListener('scroll', handleTopScroll);
      bottomScroll.removeEventListener('scroll', handleBottomScroll);
    };
  }, []);
  
  // Format number with Indian numbering system (lakhs, crores)
  const formatNumber = (num) => {
    if (!num || num === '-') return '-';

    if (Array.isArray(num)) {
      return num.map(val => formatSingleNumber(val)).join(' & ');
    }
    
    // Check if it's an array format (e.g., "1234 & 5678")
    if (num.toString().includes(' & ')) {
      const values = num.toString().split(' & ');
      return values.map(val => formatSingleNumber(val.trim())).join(' & ');
    }
    
    return formatSingleNumber(num);
  };

  const formatSingleNumber = (num) => {
    if (!num || num === '-') return '-';
    const numStr = num.toString().replace(/,/g, '');
    const parts = numStr.split('.');
    let integerPart = parts[0];
    
    // If 3 digits or less, no formatting needed
    if (integerPart.length <= 3) {
      return parts.length > 1 ? integerPart + '.' + parts[1] : integerPart;
    }
    
    let result = '';
    let count = 0;
    
    // Process from right to left: comma after 3 digits, then every 2 digits
    for (let i = integerPart.length - 1; i >= 0; i--) {
      if (count === 3 || (count > 3 && (count - 3) % 2 === 0)) {
        result = ',' + result;
      }
      result = integerPart[i] + result;
      count++;
    }
    
    return parts.length > 1 ? result + '.' + parts[1] : result;
  };

  // Remove time from date string
  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === '-') return '-';
    // Remove time part (00:00:00 or similar) and trim
    return dateStr.split(' ')[0].trim();
  };

  const handleViewPDF = (pdfUrl, pdfName) => {
    if (pdfUrl) {
      // If it's an S3 key (no http), convert to CloudFront URL
      const fullUrl = pdfUrl.startsWith('http') 
        ? pdfUrl 
        : getCloudFrontUrl(pdfUrl);
      
      // Open PDF in new tab
      if (fullUrl) {
        window.open(fullUrl, '_blank');
      } else {
        toast.error('PDF URL could not be generated');
      }
    } else {
      toast.error('PDF not available');
    }
  };

  const normalizePdfList = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed.filter(Boolean);
      } catch {
        return [value];
      }
      return [value];
    }
    return [value];
  };

  const handleEdit = (shipment) => {
    if (onEdit) {
      onEdit(shipment);
    }
  };

  const handleDelete = (shipment) => {
    if (onDelete) {
      onDelete(shipment);
    }
  };

  // Clicking a date column sorts by it; clicking the active one flips the order.
  const handleSortBy = (column) => {
    if (sortBy === column) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(column); setSortOrder('desc'); }
  };

  const getAllTimestamps = (value) => {
    if (!value || value === '-') return [];

    let values = [];
    if (Array.isArray(value)) {
      values = value;
    } else if (typeof value === 'string') {
      const trimmed = value.trim();
      try {
        const parsed = JSON.parse(trimmed);
        values = Array.isArray(parsed) ? parsed : [trimmed];
      } catch {
        if (trimmed.includes('&')) {
          values = trimmed.split(/\s*&\s*/);
        } else if (trimmed.includes(',')) {
          values = trimmed.split(',');
        } else {
          values = [trimmed];
        }
      }
    } else {
      values = [value];
    }

    const toTimestamp = (val) => {
      if (!val) return 0;
      const str = String(val).trim();

      const ddmmyyyy = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (ddmmyyyy) {
        const [, dd, mm, yyyy] = ddmmyyyy;
        return new Date(`${yyyy}-${mm}-${dd}`).getTime() || 0;
      }

      const ddmmyy = str.match(/^(\d{2})\/(\d{2})\/(\d{2})$/);
      if (ddmmyy) {
        const [, dd, mm, yy] = ddmmyy;
        const yyyy = 2000 + parseInt(yy, 10);
        return new Date(`${yyyy}-${mm}-${dd}`).getTime() || 0;
      }

      const ddmmyyDash = str.match(/^(\d{2})-(\d{2})-(\d{2,4})$/);
      if (ddmmyyDash) {
        let [, dd, mm, yy] = ddmmyyDash;
        let yyyy = parseInt(yy, 10);
        if (yyyy < 100) yyyy += 2000;
        return new Date(`${yyyy}-${mm}-${dd}`).getTime() || 0;
      }

      const ddmmmyy = str.match(/^(\d{2})-(\w{3})-(\d{2,4})$/);
      if (ddmmmyy) {
        const monthMap = {
          JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06',
          JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12'
        };
        const dd = ddmmmyy[1];
        const mmm = ddmmmyy[2].toUpperCase();
        let yyyy = parseInt(ddmmmyy[3], 10);
        if (yyyy < 100) yyyy += 2000;
        const mm = monthMap[mmm];
        if (mm) return new Date(`${yyyy}-${mm}-${dd}`).getTime() || 0;
      }

      const parsed = new Date(str).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    };

    return values.map(toTimestamp).filter((ts) => ts > 0);
  };

  const getSortTimestamp = (value, order) => {
    const timestamps = getAllTimestamps(value);
    if (!timestamps.length) return 0;
    return order === 'asc' ? Math.min(...timestamps) : Math.max(...timestamps);
  };

  const sortedShipments = [...shipments].sort((a, b) => {
    if (sortOrder === 'none') return 0;

    const stamp = (sh) => {
      if (sortBy === 'invoiceDate') {
        // The invoice date lives on the invoice record, not the shipment.
        const d = getInvoiceMoney(invoiceMap[sh.awbNumber])?.invoiceDate;
        return d ? getSortTimestamp(d, sortOrder) : 0;
      }
      return getSortTimestamp(sh.sbDate, sortOrder)
        || getSortTimestamp(sh.bkg_date || sh.created_at || sh.createdAt, sortOrder);
    };

    const tsA = stamp(a);
    const tsB = stamp(b);
    // Rows without the sorted date always sit at the bottom, either way round.
    if (!tsA && !tsB) return 0;
    if (!tsA) return 1;
    if (!tsB) return -1;
    return sortOrder === 'asc' ? tsA - tsB : tsB - tsA;
  });

  useEffect(() => {
    if (sortOrder !== 'none') {
      console.log('S/B Date sorting applied:', {
        order: sortOrder,
        sample: sortedShipments.slice(0, 10).map((s) => ({
          awbNo: s.awbNo || s.awb_no || s.awbNumber,
          sbDate: s.sbDate
        }))
      });
    } else {
      console.log('S/B Date sorting cleared (original order restored)');
    }
  }, [sortOrder, sortedShipments]);

  // Show Action column only for employee and accountant
  const showActions = userRole === 'employee' || userRole === 'accountant';

  const getRowKey = (shipment, index) => {
    const awb = shipment.awbNo || shipment.awb_no || shipment.awbNumber;
    const bl = shipment.blNumber;
    return awb || bl || `${shipment.id || 'row'}-${index}`;
  };

  return (
    <div>
      {/* Invoice Amt fixed tooltip — rendered outside overflow container */}
      {invTooltip && (
        <div
          className="fixed z-[9999] w-52 bg-slate-900 dark:bg-slate-700 text-white text-xs rounded-lg p-3 shadow-xl pointer-events-none"
          style={{ top: invTooltip.top, left: invTooltip.left, transform: 'translateX(-50%)' }}
        >
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-slate-900 dark:border-b-slate-700"></div>
          <div className="flex justify-between mb-1.5">
            <span className="text-slate-300">Taxable Amt:</span>
            <span className="font-semibold">₹{formatNumber(Math.round(invTooltip.taxable || 0))}</span>
          </div>
          {invTooltip.isIntra ? (
            <>
              <div className="flex justify-between mb-1">
                <span className="text-slate-300">CGST (9%):</span>
                <span className="font-semibold">₹{formatNumber(Math.round(invTooltip.cgst || 0))}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-300">SGST (9%):</span>
                <span className="font-semibold">₹{formatNumber(Math.round(invTooltip.sgst || 0))}</span>
              </div>
            </>
          ) : (
            <div className="flex justify-between">
              <span className="text-slate-300">IGST (18%):</span>
              <span className="font-semibold">₹{formatNumber(Math.round(invTooltip.igst || 0))}</span>
            </div>
          )}
        </div>
      )}
      {/* Top Scrollbar */}
      <div 
        ref={topScrollRef}
        className="overflow-x-auto mb-2"
        style={{ 
          overflowY: 'hidden',
          height: '10px',
          scrollbarWidth: 'thin',
          scrollbarColor: '#9ca3af #e5e7eb',
          touchAction: 'pan-x',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <div ref={topScrollContentRef} style={{ height: '1px', width: '3000px' }}></div>
      </div>
      
      {/* Main Table with Bottom Scrollbar */}
      <div 
        ref={bottomScrollRef}
        // Constraining the height turns this into a vertical scrollport, which
        // is what actually activates the sticky header and totals row.
        /* `flush` means a console panel already draws the card around us. */
        className={`overflow-x-auto bg-white dark:bg-[#0e1626] ${flush ? '' : 'rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm'} ${showTotals ? 'max-h-[calc(100vh-230px)] overflow-y-auto' : ''}`}
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#9ca3af #e5e7eb',
          touchAction: 'pan-x pan-y',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <table ref={tableRef} className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
        <thead className="bg-slate-50 dark:bg-[#111b2e] sticky top-0 z-10 border-b border-slate-200 dark:border-slate-800">
          <tr>
            <th className="px-4 py-3.5 text-center text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap w-16">
              Sr. No.
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              AWB No.
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              User
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              Consignee
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              S/B No.
            </th>
            <th
              className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/70 transition"
              onClick={() => handleSortBy('sbDate')}
            >
              <div className="flex items-center gap-2">
                <span>S/B Date</span>
                {sortBy !== 'sbDate' && <ArrowUpDown size={14} className="opacity-50" />}
                {sortBy === 'sbDate' && sortOrder === 'asc' && <ArrowUp size={14} className="text-sky-300" />}
                {sortBy === 'sbDate' && sortOrder === 'desc' && <ArrowDown size={14} className="text-sky-300" />}
              </div>
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              Origin Port Code
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              City Code
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              Dest. City
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              Dest. Country
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              Flight No
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              Flight Date
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              Total Pkt
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              Weight <span className="normal-case font-medium text-slate-300">(kg)</span>
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              CH Weight <span className="normal-case font-medium text-slate-300">(kg)</span>
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              Amount <span className="normal-case font-medium text-slate-300">(₹)</span>
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              Activity
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              FOB <span className="normal-case font-medium text-slate-300">(₹)</span>
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              DBK <span className="normal-case font-medium text-slate-300">(₹)</span>
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              Rodtep <span className="normal-case font-medium text-slate-300">(₹)</span>
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              Rosctl <span className="normal-case font-medium text-slate-300">(₹)</span>
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              IGST <span className="normal-case font-medium text-slate-300">(₹)</span>
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              FC Value <span className="normal-case font-medium text-slate-300">($)</span>
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              FC Type
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              FC Rate
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              Buyer Name
            </th>
            {(userRole === 'accountant' || userRole === 'super_admin' || userRole === 'admin') && (
              <>
                <th
                  className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-800/70 transition"
                  onClick={() => handleSortBy('invoiceDate')}
                  title="Sort by invoice date"
                >
                  <div className="flex items-center gap-2">
                    <span>Invoice Date</span>
                    {sortBy !== 'invoiceDate' && <ArrowUpDown size={14} className="opacity-50" />}
                    {sortBy === 'invoiceDate' && sortOrder === 'asc' && <ArrowUp size={14} className="text-sky-300" />}
                    {sortBy === 'invoiceDate' && sortOrder === 'desc' && <ArrowDown size={14} className="text-sky-300" />}
                  </div>
                </th>
                <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
                  Invoice No.
                </th>
                <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
                  Invoice Amt <span className="normal-case font-medium text-slate-300">(₹)</span>
                </th>
              </>
            )}
            {(userRole === 'accountant' || userRole === 'super_admin' || userRole === 'admin') && (
              <>
                <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
                  Payment
                </th>
                <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
                  Paid Amt <span className="normal-case font-medium text-slate-300">(₹)</span>
                </th>
                <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
                  Balance Due <span className="normal-case font-medium text-slate-300">(₹)</span>
                </th>
              </>
            )}
            {(userRole === 'accountant' || userRole === 'super_admin' || userRole === 'admin') && (
              <>
                <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
                  PUR Inv No.
                </th>
                <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
                  PUR Date
                </th>
                <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
                  PUR Status
                </th>
              </>
            )}
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              AWB
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              LEO
            </th>
            <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
              INV/PL
            </th>
            {(userRole === 'accountant' || userRole === 'super_admin' || userRole === 'admin') && (
              <>
                <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
                  INVOICE
                </th>
                <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
                  PUR
                </th>
              </>
            )}
            {userRole === 'super_admin' && (
              <>
                <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
                  Admin Name
                </th>
                <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
                  Emp Name
                </th>
              </>
            )}
            {showActions && (
              <th className="px-4 py-3.5 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">
                Action
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
          {sortedShipments.length === 0 ? (
            <tr>
              {/* These were three hand-counted numbers that had already drifted from
                  the real column count before the PUR pair was added. A span wider
                  than the table is clamped to it, so one number cannot go stale. */}
              <td colSpan={99} className="px-6 py-8 text-center text-slate-500 dark:text-slate-400">
                No shipments found
              </td>
            </tr>
          ) : (
            sortedShipments.map((shipment, index) => {
              const isHighlighted = highlightedAwb && shipment.awbNumber === highlightedAwb;
              return (
              <tr
                key={getRowKey(shipment, index)}
                ref={isHighlighted ? highlightRowRef : null}
                className={`hover:bg-slate-50/80 dark:hover:bg-slate-800/40 ${
                  isHighlighted
                    ? 'bg-yellow-100 dark:bg-yellow-900/30 animate-pulse ring-2 ring-yellow-400 dark:ring-yellow-500'
                    : ''
                }`}
              >
                <td className="px-4 py-3.5 text-center whitespace-nowrap text-[12px] font-bold text-slate-400 dark:text-slate-500 bg-slate-50/70 dark:bg-slate-900/40">
                  {index + 1}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] font-bold text-slate-900 dark:text-white">
                  {shipment.awbNumber || '-'}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {shipment.username || '-'}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {shipment.customerName || '-'}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {shipment.sbNumber || '-'}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {shipment.sbDate || '-'}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {shipment.portCode || '-'}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {shipment.cityCode || '-'}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {shipment.destCity || '-'}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {shipment.destCountry || '-'}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {shipment.flightNo || '-'}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {formatDate(shipment.flightDate)}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {shipment.totalPkt || '-'}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {formatNumber(shipment.weight)}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {formatNumber(shipment.chWeight)}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {formatNumber(shipment.amount)}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {shipment.activity || '-'}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {formatNumber(shipment.fob)}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {formatNumber(shipment.dbk)}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {formatNumber(shipment.rodtep)}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {formatNumber(shipment.rosctl)}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {formatNumber(shipment.igst)}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {formatNumber(shipment.fcValue)}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {shipment.fcType || '-'}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {formatNumber(shipment.fcRate)}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                  {shipment.buyerName || '-'}
                </td>
                {(userRole === 'accountant' || userRole === 'super_admin' || userRole === 'admin') && (() => {
                  const money = getInvoiceMoney(invoiceMap[shipment.awbNumber]);
                  return (
                    <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                      {money?.invoiceDate ? formatInvoiceDate(money.invoiceDate) : <span className="text-slate-400 dark:text-slate-500">—</span>}
                    </td>
                  );
                })()}
                {(userRole === 'accountant' || userRole === 'super_admin' || userRole === 'admin') && (() => {
                  const numbers = getInvoiceNumbers(invoiceMap[shipment.awbNumber]);
                  return (
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      {numbers.length ? (
                        <span className="font-mono font-semibold text-slate-700 dark:text-slate-200" title={numbers.join('\n')}>
                          {numbers[0]}
                        </span>
                      ) : (
                        <span className="text-slate-400 dark:text-slate-500">—</span>
                      )}
                    </td>
                  );
                })()}
                {(userRole === 'accountant' || userRole === 'super_admin' || userRole === 'admin') && (() => {
                  const invRecord = invoiceMap[shipment.awbNumber];
                  const raw = invRecord && (Array.isArray(invRecord) ? invRecord[0] : invRecord);
                  if (!raw) return <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-400 dark:text-slate-500">—</td>;
                  const inv = raw.invoice || raw;
                  const summary = inv.summary || inv;
                  const grandTotal = summary.grand_total ?? summary.grandTotal ?? null;
                  if (grandTotal == null) {
                    return <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-400 dark:text-slate-500">—</td>;
                  }
                  const taxable = summary.taxable_amount ?? summary.taxableAmount ?? 0;
                  const igst  = summary.total_igst  ?? summary.totalIgst  ?? 0;
                  const cgst  = summary.total_cgst  ?? summary.totalCgst  ?? 0;
                  const sgst  = summary.total_sgst  ?? summary.totalSgst  ?? 0;
                  const isIntra = cgst > 0 || sgst > 0;
                  return (
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <span
                        className="font-semibold text-emerald-600 dark:text-emerald-400 cursor-default select-none"
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setInvTooltip({ top: rect.bottom + 8, left: rect.left + rect.width / 2, taxable, igst, cgst, sgst, isIntra });
                        }}
                        onMouseLeave={() => setInvTooltip(null)}
                      >
                        ₹{formatNumber(Math.round(grandTotal || 0))}
                      </span>
                    </td>
                  );
                })()}
                {(userRole === 'accountant' || userRole === 'super_admin' || userRole === 'admin') && (() => {
                  const dash = <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-400 dark:text-slate-500">—</td>;
                  const invRecord = invoiceMap[shipment.awbNumber];
                  const raw = invRecord && (Array.isArray(invRecord) ? invRecord[0] : invRecord);
                  if (!raw) return <>{dash}{dash}{dash}</>;
                  const { status, paidSoFar, balance } = getInvoiceMoney(raw);
                  const style = INVOICE_STATUS_STYLES[status] || INVOICE_STATUS_STYLES.unpaid;
                  return (
                    <>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${style.chip}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                            {style.label}
                          </span>
                          {onUpdatePayment && (
                            <button
                              type="button"
                              onClick={() => onUpdatePayment(shipment, invRecord)}
                              className="p-1 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition"
                              title="Update payment"
                              aria-label={`Update payment for ${shipment.awbNumber}`}
                            >
                              <Edit size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className={`px-4 py-4 whitespace-nowrap text-sm font-semibold ${paidSoFar > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'}`}>
                        {paidSoFar > 0 ? `₹${formatNumber(Math.round(paidSoFar))}` : '—'}
                      </td>
                      <td className={`px-4 py-4 whitespace-nowrap text-sm font-semibold ${balance > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-400 dark:text-slate-500'}`}>
                        {balance > 0 ? `₹${formatNumber(Math.round(balance))}` : (status === 'paid' ? '₹0' : '—')}
                      </td>
                    </>
                  );
                })()}
                {/* The purchase bill filed against this shipment. The number opens it. */}
                {(userRole === 'accountant' || userRole === 'super_admin' || userRole === 'admin') && (() => {
                  const pur = purOf(purMap, shipment);
                  const dash = <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-400 dark:text-slate-500">—</td>;
                  if (!pur) return <>{dash}{dash}{dash}</>;
                  return (
                    <>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <button
                          type="button"
                          onClick={() => onViewPur && onViewPur(pur)}
                          title={`Open ${pur.invoiceNumber || 'this purchase bill'}`}
                          className="inline-flex items-center gap-1.5 font-mono font-semibold text-violet-700 dark:text-violet-300
                                     hover:underline underline-offset-2 disabled:no-underline"
                          disabled={!onViewPur}
                        >
                          {pur.invoiceNumber || '(no number)'}
                          {pur.extractionStatus === 'needs_review' && (
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" title="The reader was unsure about this bill" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300 tabular-nums">
                        {pur.invoiceDate || '—'}
                      </td>
                      {/* How much of the bill has been paid, which the extraction
                          dot beside the number says nothing about. */}
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {(() => {
                          const look = INVOICE_STATUS_STYLES[pur.status] || INVOICE_STATUS_STYLES.unpaid;
                          return (
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${look.chip}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${look.dot}`} />
                              {look.label}
                            </span>
                          );
                        })()}
                      </td>
                    </>
                  );
                })()}
                <td className="px-4 py-4 whitespace-nowrap text-sm">
                  {(shipment.awbPdf || shipment.awb) ? (
                    <button
                      onClick={() => handleViewPDF(shipment.awbPdf || shipment.awb, 'AWB Copy')}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                    >
                      <FileText size={18} />
                    </button>
                  ) : (
                    <span className="text-slate-400">-</span>
                  )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm">
                  {normalizePdfList(shipment.leo).length > 0 ? (
                    <div className="flex items-center gap-2">
                      {normalizePdfList(shipment.leo).map((leoPdf, index) => (
                        <button
                          key={`${leoPdf}-${index}`}
                          onClick={() => handleViewPDF(leoPdf, `LEO ${index + 1}`)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                          title={`LEO ${index + 1}`}
                        >
                          <FileText size={18} />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <span className="text-slate-400">-</span>
                  )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm">
                  {normalizePdfList(shipment.invPdf || shipment.inv).length > 0 ? (
                    <div className="flex items-center gap-2">
                      {normalizePdfList(shipment.invPdf || shipment.inv).map((pdfUrl, idx) => (
                        <button
                          key={`${pdfUrl}-${idx}`}
                          onClick={() => handleViewPDF(pdfUrl, `Invoice/Packing List ${idx + 1}`)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                          title={`Invoice/Packing List ${idx + 1}`}
                        >
                          <FileText size={18} />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <span className="text-slate-400">-</span>
                  )}
                </td>
                {(userRole === 'accountant' || userRole === 'super_admin' || userRole === 'admin') && (
                  <>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        {shipment.invoice && (
                          <button
                            onClick={() => handleViewPDF(shipment.invoice, 'Invoice')}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                          >
                            <FileText size={18} />
                          </button>
                        )}
                        {(userRole === 'accountant' || userRole === 'super_admin') && (
                          invoiceMap[shipment.awbNumber] ? (
                            <button
                              className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition text-xs font-medium flex items-center gap-1"
                              onClick={() => onEditInvoice && onEditInvoice(shipment, invoiceMap[shipment.awbNumber])}
                            >
                              <FileDown size={14} />
                              Revise
                            </button>
                          ) : (
                            <button
                              className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition text-xs font-medium flex items-center gap-1"
                              onClick={() => onGenerateInvoice && onGenerateInvoice(shipment)}
                            >
                              <FileDown size={14} />
                              Generate
                            </button>
                          )
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      {shipment.pur ? (
                        <button
                          onClick={() => handleViewPDF(shipment.pur, 'PUR')}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                        >
                          <FileText size={18} />
                        </button>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                  </>
                )}
                {userRole === 'super_admin' && (
                  <>
                    <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                      {shipment.adminName || '-'}
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                      {shipment.editedBy || '-'}
                    </td>
                  </>
                )}
                {showActions && (
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center gap-2">
                      <button
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition text-xs font-medium flex items-center gap-1"
                        onClick={() => handleEdit(shipment)}
                      >
                        <Edit size={14} />
                        Edit
                      </button>
                      {userRole === 'employee' && (
                        <button
                          className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition text-xs font-medium flex items-center gap-1"
                          onClick={() => handleDelete(shipment)}
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
              );
            })
          )}
        </tbody>
        {showTotals && sortedShipments.length > 0 && (() => {
          const showInvoiceCols = userRole === 'accountant' || userRole === 'super_admin' || userRole === 'admin';
          const sum = (getter) => sortedShipments.reduce((acc, sh) => acc + toNumericTotal(getter(sh)), 0);
          const money = sortedShipments.map((sh) => getInvoiceMoney(invoiceMap[sh.awbNumber])).filter(Boolean);
          const sumMoney = (key) => money.reduce((acc, m) => acc + m[key], 0);
          // Mirrors the header's look so the row reads as the table's own footer.
          const cell = 'px-4 py-3.5 whitespace-nowrap text-sm font-bold text-white sticky bottom-0 z-10 bg-slate-800 dark:bg-slate-950';
          // formatNumber() renders 0 as '-', which reads wrong in a totals row.
          const fmtTotal = (n) => { const r = Math.round(n * 100) / 100; return r === 0 ? '0' : formatNumber(r); };
          const rupees = (n) => `₹${fmtTotal(Math.round(n))}`;
          return (
            <tfoot>
              <tr>
                <td colSpan={12} className={`${cell} text-slate-300 uppercase tracking-wider text-xs`}>
                  Total — {sortedShipments.length} shipment{sortedShipments.length > 1 ? 's' : ''}
                </td>
                <td className={cell}>{fmtTotal(sum((sh) => sh.totalPkt))}</td>
                <td className={cell}>{fmtTotal(sum((sh) => sh.weight))}</td>
                <td className={cell}>{fmtTotal(sum((sh) => sh.chWeight))}</td>
                <td className={cell}>{rupees(sum((sh) => sh.amount))}</td>
                {/* Activity, then the duty / value columns the row prints in the
                    same order: FOB, DBK, Rodtep, Rosctl, IGST, FC Value. */}
                <td className={cell} />
                <td className={cell}>{rupees(sum((sh) => sh.fob))}</td>
                <td className={cell}>{rupees(sum((sh) => sh.dbk))}</td>
                <td className={cell}>{rupees(sum((sh) => sh.rodtep))}</td>
                <td className={cell}>{rupees(sum((sh) => sh.rosctl))}</td>
                <td className={cell}>{rupees(sum((sh) => sh.igst))}</td>
                {/* In the foreign currency, so no rupee sign on it. */}
                <td className={cell}>{fmtTotal(sum((sh) => sh.fcValue))}</td>
                <td colSpan={3} className={cell} />
                {showInvoiceCols && (
                  <>
                    <td colSpan={2} className={cell} />
                    <td className={`${cell} text-emerald-300`}>{rupees(sumMoney('grandTotal'))}</td>
                    <td className={cell} />
                    <td className={`${cell} text-emerald-300`}>{rupees(sumMoney('paidSoFar'))}</td>
                    <td className={`${cell} text-red-300`}>{rupees(sumMoney('balance'))}</td>
                  </>
                )}
                {/* The PUR pair, then AWB / LEO / INV-PL. */}
                <td colSpan={showInvoiceCols ? 6 : 3} className={cell} />
                {showInvoiceCols && <td colSpan={2} className={cell} />}
                {userRole === 'super_admin' && <td colSpan={2} className={cell} />}
                {showActions && <td className={cell} />}
              </tr>
            </tfoot>
          );
        })()}
      </table>
      </div>
    </div>
  );
};

export default ShipmentsTable;
